/**
 * ClearShiftMenu — take one shift off a roster cell.
 *
 * The only edit the board offers on a cell: clearing it cancels the shift row.
 * A tap opens this chooser first so a stray tap never cancels a shift.
 *
 * Two form factors, one behaviour:
 *   - mobile (< md): a Radix bottom sheet (modal, focus-trapped, Escape-safe) in
 *     the thumb zone with safe-area padding.
 *   - desktop (≥ md): a non-modal popover anchored to the tapped cell so the row
 *     context stays visible; closes on outside click, Escape, or scroll/resize.
 * The action is ≥ 44px and carries an icon + text (never colour alone).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useIsDesktop } from './useIsDesktop'

const POPOVER_WIDTH = 264
const POPOVER_MARGIN = 8

export interface ClearShiftMenuProps {
  /** Cell context for the title, e.g. "Beverly · Mi 14.07 · F". */
  title: string
  onClear: () => void
  onClose: () => void
  /** Desktop popover anchor (the tapped cell's bounding rect). */
  anchorRect: DOMRect | null
}

function ClearButton({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onClear}
      className="flex min-h-11 w-full items-center gap-2 rounded-[11px] border border-dashed border-[var(--color-surface-border)] px-3 py-2 text-[14px] text-[var(--color-surface-mute)] transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]"
    >
      <X className="h-4 w-4 shrink-0" aria-hidden="true" />
      {t('roster.picker.clear', 'Clear (off duty)')}
    </button>
  )
}

export function ClearShiftMenu({ title, onClear, onClose, anchorRect }: ClearShiftMenuProps) {
  const isDesktop = useIsDesktop()
  const panelRef = useRef<HTMLDivElement>(null)

  const usePopover = isDesktop && !!anchorRect

  // Desktop popover: position from the anchor rect, flipping above when the
  // cell sits low in the viewport.
  const [style, setStyle] = useState<React.CSSProperties>({})
  useLayoutEffect(() => {
    if (!usePopover || !anchorRect) return
    const left = Math.min(
      Math.max(POPOVER_MARGIN, anchorRect.left),
      window.innerWidth - POPOVER_WIDTH - POPOVER_MARGIN,
    )
    const spaceBelow = window.innerHeight - anchorRect.bottom
    const openUp = spaceBelow < 160
    setStyle({
      position: 'fixed',
      width: POPOVER_WIDTH,
      left,
      ...(openUp
        ? { bottom: window.innerHeight - anchorRect.top + POPOVER_MARGIN }
        : { top: anchorRect.bottom + POPOVER_MARGIN }),
    })
  }, [usePopover, anchorRect])

  // Desktop popover a11y: focus the action, close on Escape / outside click /
  // scroll / resize (a non-modal transient chooser — no focus trap).
  useEffect(() => {
    if (!usePopover) return
    panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onPointer = (e: PointerEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    window.addEventListener('scroll', onClose, true)
    window.addEventListener('resize', onClose)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
      window.removeEventListener('scroll', onClose, true)
      window.removeEventListener('resize', onClose)
    }
  }, [usePopover, onClose])

  if (usePopover) {
    return createPortal(
      <div
        ref={panelRef}
        role="dialog"
        aria-label={title}
        style={style}
        className="z-50 rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] p-3"
      >
        <p className="mb-2 truncate px-1 text-[13px] font-semibold text-[var(--color-surface-ink)]">
          {title}
        </p>
        <ClearButton onClear={onClear} />
      </div>,
      document.body,
    )
  }

  // Mobile: Radix bottom sheet (modal, accessible) in the thumb zone.
  return (
    <Sheet open onOpenChange={(next) => { if (!next) onClose() }}>
      <SheetContent
        side="bottom"
        className="rounded-t-[18px] border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] p-4 pb-safe"
      >
        <SheetHeader className="mb-3">
          <SheetTitle className="text-[17px] font-semibold tracking-[-0.01em] text-[var(--color-surface-ink)]">
            {title}
          </SheetTitle>
        </SheetHeader>
        <ClearButton onClear={onClear} />
      </SheetContent>
    </Sheet>
  )
}
